import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { LanguageProvider, detectLang } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import SiteShell from "@/components/SiteShell";
import LanguageSync from "@/components/LanguageSync";

import Index from "./pages/Index";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import ProfileSettings from "./pages/ProfileSettings";
import ResetPassword from "./pages/ResetPassword";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import Admin from "./pages/Admin";
import PaymentComplete from "./pages/PaymentComplete";
import LegalPage, { HowItWorks } from "./pages/Legal";
import Brand from "./pages/Brand";
import NotFound from "./pages/NotFound";
import AlertUnsubscribe from "./pages/AlertUnsubscribe";




// ---------------------------------------------------------------------------
// PROGRAMMATIC SEO / GEO / AEO PAGES
// 50 trades + 58 counties + 200+ cities + 100+ money pages + 75 guides +
// hubs and core pages. All driven by the catalogs in src/data/seo/*, so the
// architecture scales to thousands of URLs without new route code.
// ---------------------------------------------------------------------------
import TradePage from "./pages/seo/TradePage";
import CountyPage from "./pages/seo/CountyPage";
import CityPage from "./pages/seo/CityPage";
import MoneyPage from "./pages/seo/MoneyPage";
import ResourcePage from "./pages/seo/ResourcePage";
import {
  TradesHub, CaliforniaHub, LeadsHub, ResourcesHub, ProjectDirectory, ContractorDirectory,
} from "./pages/seo/Hubs";
import {
  AboutPage, MembershipsPage, AudiencePage, PostProjectPage, FaqPage,
} from "./pages/seo/CorePages";




const queryClient = new QueryClient();

/** Sends any non-prefixed URL to the detected language version. */
const LangRedirect = () => {
  const location = useLocation();
  const lang = detectLang();
  const target = `/${lang}${location.pathname === "/" ? "" : location.pathname}${location.search}`;
  return <Navigate to={target} replace />;
};

const localizedRoutes = (
  <>
    <Route index element={<Index />} />
    <Route path="jobs" element={<Jobs />} />
    <Route path="jobs/:slug" element={<JobDetail />} />
    <Route path="trabajos" element={<Jobs />} />
    <Route path="trabajos/:slug" element={<JobDetail />} />
    <Route path="marketplace" element={<Jobs />} />
    <Route path="pricing" element={<Pricing />} />
    <Route path="precios" element={<Pricing />} />
    <Route path="how-it-works" element={<HowItWorks />} />
    <Route path="como-funciona" element={<HowItWorks />} />
    <Route path="login" element={<Login />} />
    <Route path="iniciar-sesion" element={<Login />} />
    <Route path="register" element={<Register />} />
    <Route path="registro" element={<Register />} />
    <Route path="dashboard" element={<Dashboard />} />
    <Route path="panel" element={<Dashboard />} />
    <Route path="profile" element={<ProfileSettings />} />
    <Route path="perfil" element={<ProfileSettings />} />
    <Route path="reset-password" element={<ResetPassword />} />
    <Route path="restablecer-contrasena" element={<ResetPassword />} />
    <Route path="contact" element={<Contact />} />
    <Route path="contacto" element={<Contact />} />

    {/*
      CHECKOUT RETURN. The hosted checkout's success redirect points here, so the
      customer comes back to GigGone instead of sitting on the payment receipt.
      The page waits for the verified webhook, activates the plan credits
      server-side and then continues to Projects automatically.
    */}
    <Route path="payment-complete" element={<PaymentComplete />} />
    <Route path="pago-completado" element={<PaymentComplete />} />


    {/* ---- Trade pages (50) ---- */}
    <Route path="trades" element={<TradesHub />} />
    <Route path="trades/:slug" element={<TradePage />} />
    <Route path="oficios" element={<TradesHub />} />
    <Route path="oficios/:slug" element={<TradePage />} />

    {/* ---- Location hierarchy: California -> county -> city ---- */}
    <Route path="california" element={<CaliforniaHub />} />
    <Route path="california/counties/:slug" element={<CountyPage />} />
    <Route path="california/condados/:slug" element={<CountyPage />} />
    <Route path="california/:slug" element={<CityPage />} />

    {/* ---- Money pages (100+) ---- */}
    <Route path="leads" element={<LeadsHub />} />
    <Route path="leads/:slug" element={<MoneyPage />} />

    {/* ---- Resource Center (75) + blog alias ---- */}
    <Route path="resources" element={<ResourcesHub />} />
    <Route path="resources/:slug" element={<ResourcePage />} />
    <Route path="recursos" element={<ResourcesHub />} />
    <Route path="recursos/:slug" element={<ResourcePage />} />
    <Route path="blog" element={<ResourcesHub />} />

    {/* ---- Directories ---- */}
    <Route path="project-directory" element={<ProjectDirectory />} />
    <Route path="directorio-de-proyectos" element={<ProjectDirectory />} />
    <Route path="contractor-directory" element={<ContractorDirectory />} />
    <Route path="directorio-de-contratistas" element={<ContractorDirectory />} />

    {/* ---- Core marketplace pages ---- */}
    <Route path="about" element={<AboutPage />} />
    <Route path="acerca-de" element={<AboutPage />} />
    <Route path="memberships" element={<MembershipsPage />} />
    <Route path="membresias" element={<MembershipsPage />} />
    <Route path="general-contractors" element={<AudiencePage who="gc" />} />
    <Route path="contratistas-generales" element={<AudiencePage who="gc" />} />
    <Route path="subcontractors" element={<AudiencePage who="sub" />} />
    <Route path="subcontratistas" element={<AudiencePage who="sub" />} />
    <Route path="homeowners" element={<AudiencePage who="homeowner" />} />
    <Route path="propietarios" element={<AudiencePage who="homeowner" />} />
    {/* Commercial property owners / property managers / businesses. */}
    <Route path="commercial" element={<AudiencePage who="commercial" />} />
    <Route path="comercial" element={<AudiencePage who="commercial" />} />

    <Route path="post-a-project" element={<PostProjectPage />} />
    <Route path="publicar-proyecto" element={<PostProjectPage />} />
    <Route path="faq" element={<FaqPage />} />
    <Route path="preguntas-frecuentes" element={<FaqPage />} />
    <Route path="help" element={<FaqPage help />} />
    <Route path="ayuda" element={<FaqPage help />} />

    <Route path="admin" element={<Admin />} />
    <Route path="administrador" element={<Admin />} />

    {/* Brand studio: internal review of the five logo concepts at favicon,
        app-icon and header sizes on light and dark. noindex via <Seo>. */}
    <Route path="brand" element={<Brand />} />
    <Route path="marca" element={<Brand />} />

    {/* One-click job-alert unsubscribe. Token-based capability URL from the
        digest email — no login required, noindex via <Seo>. */}
    <Route path="alerts/unsubscribe" element={<AlertUnsubscribe />} />
    <Route path="alertas/cancelar" element={<AlertUnsubscribe />} />


    <Route path="privacy" element={<LegalPage kind="privacy" />} />
    <Route path="privacidad" element={<LegalPage kind="privacy" />} />
    <Route path="terms" element={<LegalPage kind="terms" />} />
    <Route path="terminos" element={<LegalPage kind="terms" />} />
    <Route path="*" element={<NotFound />} />
  </>
);


const App = () => (
  <ThemeProvider defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <LanguageSync />
              <Routes>
                <Route path="/en" element={<SiteShell />}>{localizedRoutes}</Route>
                <Route path="/es" element={<SiteShell />}>{localizedRoutes}</Route>
                <Route path="*" element={<LangRedirect />} />
              </Routes>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);


export default App;
